import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { deliveryApi, barrierApi } from '../../services/api';
import { useT } from '../../i18n';
import { formatDate as _formatDateBaku } from '../../utils/date';

const STATUS_CFG = {
  pending:   { labelKey: 'del_status_pending',   color: '#f59e0b' },
  at_gate:   { labelKey: 'del_status_at_gate',   color: '#3b82f6' },
  delivered: { labelKey: 'del_status_delivered', color: '#10b981' },
  returned:  { labelKey: 'del_status_returned',  color: '#ef4444' },
};

const STATUS_FLOW = ['pending', 'at_gate', 'delivered'];

const EMPTY_FORM = {
  recipientPhone: '', courierName: '', courierPhone: '',
  packageDescription: '', barrierId: '', notes: '',
};

function fmtDate(dt) {
  return _formatDateBaku(dt, 'datetime');
}

function StatusBadge({ status }) {
  const t = useT();
  const cfg = STATUS_CFG[status] || { labelKey: status, color: 'var(--text2)' };
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      color: cfg.color, background: cfg.color + '18', border: `1px solid ${cfg.color}35`,
      whiteSpace: 'nowrap',
    }}>
      {t(cfg.labelKey) || status}
    </span>
  );
}

export default function Delivery() {
  const t = useT();
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null);
  const [newMode,    setNewMode]    = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [barriers,   setBarriers]   = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [copied,     setCopied]     = useState(false);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const d = await deliveryApi.list(status === 'all' ? '' : status);
      setItems(Array.isArray(d) ? d : []);
    } catch {
      toast.error('Çatdırılmalar yüklənmədi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(statusFilter);
  }, [load, statusFilter]);

  useEffect(() => {
    barrierApi.list()
      .then(d => setBarriers(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const openNew = () => {
    setSelected(null);
    setForm(EMPTY_FORM);
    setNewMode(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.recipientPhone.trim()) { toast.error('Alıcı nömrəsi tələb olunur'); return; }
    setSaving(true);
    try {
      const payload = {
        recipientPhone:    form.recipientPhone.trim(),
        courierName:       form.courierName.trim()       || undefined,
        courierPhone:      form.courierPhone.trim()      || undefined,
        packageDescription:form.packageDescription.trim()|| undefined,
        barrierId:         form.barrierId                || undefined,
        notes:             form.notes.trim()             || undefined,
      };
      const c = await deliveryApi.create(payload);
      setItems(p => [c, ...p]);
      setSelected(c);
      setNewMode(false);
      toast.success(t('del_new') + ' ✓ — ' + c.trackingCode);
    } catch (err) {
      toast.error(err?.message || 'Xəta baş verdi');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selected) return;
    try {
      const u = await deliveryApi.updateStatus(selected.id, status);
      setItems(p => p.filter(x => x.id !== u.id || statusFilter === 'all' || statusFilter === u.status));
      setSelected(u);
      const cfg = STATUS_CFG[status];
      toast.success(`${t('status')}: ${cfg ? t(cfg.labelKey) : status}`);
    } catch (err) {
      toast.error(err?.message || 'Status dəyişdirilmədi');
    }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm(t('confirm_delete'))) return;
    try {
      await deliveryApi.remove(selected.id);
      setItems(p => p.filter(x => x.id !== selected.id));
      setSelected(null);
      toast.success(t('delete') + ' ✓');
    } catch (err) {
      toast.error(err?.message || 'Silmə xətası');
    }
  };

  const copyTracking = () => {
    if (!selected?.trackingCode) return;
    navigator.clipboard.writeText(selected.trackingCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const showForm = newMode || !!selected;

  return (
    <div style={S.root}>

      {/* ══ LEFT PANEL ══ */}
      <div style={S.left}>
        <div style={S.leftHead}>
          <div>
            <h1 style={S.title}>{t('del_title')}</h1>
            <p style={S.sub}>{items.length} sifariş</p>
          </div>
          <button style={S.addBtn} onClick={openNew}>+ {t('del_new')}</button>
        </div>

        {/* Status filter */}
        <div style={S.filterRow}>
          {[
            ['all', t('all')],
            ['pending', t('del_status_pending')],
            ['at_gate', t('del_status_at_gate')],
            ['delivered', t('del_status_delivered')],
            ['returned', t('del_status_returned')],
          ].map(([k, l]) => {
            const cfg = STATUS_CFG[k];
            return (
              <button key={k}
                style={{
                  ...S.filterBtn,
                  color:       statusFilter === k ? (cfg?.color || 'var(--primary)') : 'var(--text3)',
                  borderColor: statusFilter === k ? (cfg?.color || 'var(--primary)') + '60' : 'transparent',
                  background:  statusFilter === k ? (cfg?.color || 'var(--primary)') + '15' : 'transparent',
                }}
                onClick={() => setStatusFilter(k)}>
                {l}
              </button>
            );
          })}
        </div>

        {/* List */}
        {loading ? (
          <div style={S.hint}>{t('loading')}</div>
        ) : items.length === 0 ? (
          <div style={S.empty}>Sifariş yoxdur</div>
        ) : items.map(item => {
          const isSel = selected?.id === item.id && !newMode;
          return (
            <div key={item.id}
              style={{ ...S.item, ...(isSel ? S.itemSel : {}) }}
              onClick={() => { setNewMode(false); setSelected(item); }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.itemCode}>{item.trackingCode || '—'}</div>
                <div style={S.itemPhone}>{item.recipientPhone}</div>
                {item.courierName && <div style={S.itemMeta}>Kuryer: {item.courierName}</div>}
                <div style={{ marginTop: 4 }}>
                  <StatusBadge status={item.status} />
                </div>
              </div>
              <div style={S.itemDate}>{fmtDate(item.createdAt)}</div>
            </div>
          );
        })}
      </div>

      {/* ══ RIGHT PANEL ══ */}
      <div style={S.right}>
        {newMode ? (
          /* CREATE FORM */
          <form onSubmit={handleSubmit} style={S.formWrap}>
            <div style={S.formHead}>
              <div style={S.formTitle}>{t('del_new')}</div>
              <button type="button" style={S.closeBtn} onClick={() => setNewMode(false)}>✕</button>
            </div>
            <div style={S.formBody}>
              <div style={S.lbl}>Alıcı nömrəsi *</div>
              <input style={S.inp} type="tel" placeholder="+994501234567"
                value={form.recipientPhone}
                onChange={e => setForm(f => ({ ...f, recipientPhone: e.target.value }))}
                required />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <div style={S.lbl}>Kuryer adı</div>
                  <input style={S.inp} placeholder="Ad Soyad"
                    value={form.courierName}
                    onChange={e => setForm(f => ({ ...f, courierName: e.target.value }))} />
                </div>
                <div>
                  <div style={S.lbl}>Kuryer nömrəsi</div>
                  <input style={S.inp} type="tel" placeholder="+994..."
                    value={form.courierPhone}
                    onChange={e => setForm(f => ({ ...f, courierPhone: e.target.value }))} />
                </div>
              </div>

              <div style={S.lbl}>Bağlama təsviri</div>
              <textarea style={{ ...S.inp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="Bağlamanın məzmunu..."
                value={form.packageDescription}
                onChange={e => setForm(f => ({ ...f, packageDescription: e.target.value }))} />

              <div style={S.lbl}>Şlaqbaum</div>
              <select style={S.inp} value={form.barrierId}
                onChange={e => setForm(f => ({ ...f, barrierId: e.target.value }))}>
                <option value="">— Seçin (opsional) —</option>
                {barriers.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>

              <div style={S.lbl}>Qeyd</div>
              <textarea style={{ ...S.inp, minHeight: 50, resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="Əlavə qeyd..."
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />

              <button type="submit" style={S.btn} disabled={saving}>
                {saving ? t('saving') : '+ ' + t('del_new')}
              </button>
            </div>
          </form>
        ) : selected ? (
          /* DETAIL VIEW */
          <div style={S.formWrap}>
            <div style={S.formHead}>
              <div>
                <div style={S.formTitle}>Sifariş Detalları</div>
                <StatusBadge status={selected.status} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={S.btnDel} onClick={handleDelete}>{t('delete')}</button>
                <button type="button" style={S.closeBtn} onClick={() => setSelected(null)}>✕</button>
              </div>
            </div>
            <div style={S.formBody}>
              {/* Tracking code */}
              <div style={S.lbl}>İzləmə Kodu</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={S.codeBox}>{selected.trackingCode}</div>
                <button type="button" style={{ ...S.btn, flexShrink: 0 }} onClick={copyTracking}>
                  {copied ? '✓' : '📋'}
                </button>
              </div>

              {/* Status timeline */}
              <div style={S.lbl}>Status yolu</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 8 }}>
                {STATUS_FLOW.map((s, i) => {
                  const cfg = STATUS_CFG[s];
                  const isActive = selected.status === s;
                  const isPast = STATUS_FLOW.indexOf(selected.status) > i;
                  return (
                    <React.Fragment key={s}>
                      <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', fontSize: 12,
                          background: (isActive || isPast) ? cfg.color + '25' : 'var(--bg2)',
                          border: `2px solid ${(isActive || isPast) ? cfg.color : 'var(--border)'}`,
                          color: (isActive || isPast) ? cfg.color : 'var(--text3)',
                          fontWeight: 800,
                        }}>
                          {isPast ? '✓' : i + 1}
                        </div>
                        <span style={{ fontSize: 9, color: isActive ? cfg.color : 'var(--text3)', fontWeight: isActive ? 700 : 400, whiteSpace: 'nowrap' }}>
                          {t(cfg.labelKey)}
                        </span>
                      </div>
                      {i < STATUS_FLOW.length - 1 && (
                        <div style={{ flex: 1, height: 2, background: isPast ? '#10b981' : 'var(--border)', marginBottom: 20 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Status update buttons */}
              {selected.status !== 'delivered' && selected.status !== 'returned' && (
                <>
                  <div style={S.lbl}>Status Yenilə</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {selected.status === 'pending' && (
                      <button style={{ ...S.statusBtn, borderColor: '#3b82f6', color: '#3b82f6', background: '#3b82f615' }}
                        onClick={() => handleUpdateStatus('at_gate')}>
                        📦 Qapıdadır
                      </button>
                    )}
                    {(selected.status === 'pending' || selected.status === 'at_gate') && (
                      <button style={{ ...S.statusBtn, borderColor: '#10b981', color: '#10b981', background: '#10b98115' }}
                        onClick={() => handleUpdateStatus('delivered')}>
                        ✓ Çatdırıldı
                      </button>
                    )}
                    <button style={{ ...S.statusBtn, borderColor: '#ef4444', color: '#ef4444', background: '#ef444415' }}
                      onClick={() => handleUpdateStatus('returned')}>
                      ↩ Qaytarıldı
                    </button>
                  </div>
                </>
              )}

              {/* Info rows */}
              {[
                ['Alıcı nömrəsi', selected.recipientPhone],
                ['Kuryer',        selected.courierName || '—'],
                ['Kuryer nömrəsi',selected.courierPhone || '—'],
                ['Bağlama',       selected.packageDescription || '—'],
                ['Qeyd',          selected.notes || '—'],
                ['Tarix',         fmtDate(selected.createdAt)],
              ].map(([k, v]) => (
                <div key={k} style={S.infoRow}>
                  <span style={S.infoK}>{k}</span>
                  <span style={S.infoV}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={S.emptyState}>
            <div style={S.emptyIcon}>📦</div>
            <div style={S.emptyMsg}>Sifariş seçin</div>
            <div style={S.emptySub}>və ya "+ {t('del_new')}" düyməsinə basın</div>
          </div>
        )}
      </div>

    </div>
  );
}

const S = {
  root:  { display: 'grid', gridTemplateColumns: '360px 1fr', gap: 20, height: 'calc(100vh - 64px)' },
  left:  { display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', paddingRight: 2 },
  right: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column' },

  leftHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 4 },
  title:     { fontSize: 20, fontWeight: 800, color: 'var(--text)', marginBottom: 2 },
  sub:       { color: 'var(--text3)', fontSize: 12 },
  addBtn:    { background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },

  filterRow: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 },
  filterBtn: { padding: '4px 9px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid', transition: 'all 0.15s' },

  item:     { display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer', transition: 'all 0.15s' },
  itemSel:  { borderColor: 'var(--primary)', background: 'var(--primary)08' },
  itemCode: { fontSize: 13, fontWeight: 800, color: 'var(--text)', fontFamily: 'monospace' },
  itemPhone: { fontSize: 11, color: 'var(--text2)', marginTop: 2 },
  itemMeta: { fontSize: 10, color: 'var(--text3)', marginTop: 1 },
  itemDate: { fontSize: 10, color: 'var(--text3)', flexShrink: 0, marginTop: 2 },

  hint:  { textAlign: 'center', color: 'var(--text3)', padding: 24, fontSize: 12 },
  empty: { textAlign: 'center', color: 'var(--text3)', padding: 24, background: 'var(--bg2)', borderRadius: 10, border: '1px dashed var(--border)', fontSize: 12 },

  formWrap: { display: 'flex', flexDirection: 'column', height: '100%' },
  formHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  formTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text)', marginBottom: 4 },
  closeBtn:  { background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 17, padding: '4px 6px' },
  formBody:  { flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 10 },

  lbl: { fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 },
  inp: { background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 11px', color: 'var(--text)', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  btn: { background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 7, padding: '9px 16px', fontWeight: 700, cursor: 'pointer', fontSize: 13 },
  btnDel: { background: 'var(--danger)15', color: 'var(--danger)', border: '1px solid var(--danger)25', borderRadius: 7, padding: '7px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 12 },
  statusBtn: { padding: '7px 14px', borderRadius: 7, border: '1px solid', fontWeight: 700, cursor: 'pointer', fontSize: 12, background: 'transparent' },

  codeBox: { flex: 1, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 14px', fontFamily: 'monospace', fontSize: 15, fontWeight: 800, color: 'var(--primary)', letterSpacing: '0.1em' },

  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--border)20' },
  infoK:   { fontSize: 12, color: 'var(--text3)', fontWeight: 700 },
  infoV:   { fontSize: 13, color: 'var(--text)', maxWidth: '65%', textAlign: 'right', wordBreak: 'break-word' },

  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 },
  emptyIcon:  { fontSize: 44, opacity: 0.15 },
  emptyMsg:   { color: 'var(--text3)', fontSize: 14, fontWeight: 600 },
  emptySub:   { color: 'var(--text3)', fontSize: 11, opacity: 0.6 },
};
